'use client'

import type { FC } from 'react'
import { Trash2 } from 'lucide-react'
import { v4 as uuidv4 } from 'uuid'
import { Button } from 'components/ui/button'
import { Input } from 'ui/input'
import { Label } from 'ui/label'
import { Textarea } from 'components/ui/textarea'
import FieldWrapper from './FieldWrapper'
import type { FieldRendererProps } from './types'

export const faqArrayFieldConfig = {
    getNewArrayItem: () => ({ id: uuidv4(), question: '', answer: '' }),
    extraPropsKind: 'array' as const
} satisfies Partial<import('./types').FieldTypeConfig>

const FaqArrayField: FC<FieldRendererProps> = ({
    field,
    value,
    error,
    formState,
    onArrayChange,
    onAddArrayItem,
    onRemoveArrayItem
}) => {
    const items = (value || []) as { id?: string; question?: string; answer?: string }[]
    const arr = (formState?.[field.name] ?? items) as unknown[]

    return (
        <FieldWrapper fieldName={field.name} error={error}>
            <div className='space-y-2'>
                <Label>{field.label}</Label>
                {items.map((item: typeof items[0], index: number) => {
                    const it = { ...item }
                    if (!it.id) it.id = uuidv4()
                    const questionLength = it.question?.length || 0
                    const answerLength = it.answer?.length || 0
                    const isQuestionValid = questionLength <= 100
                    const isAnswerValid = answerLength <= 500

                    return (
                        <div
                            key={it.id}
                            className='flex flex-col space-y-2 p-4 border border-border rounded-md mb-4'
                        >
                            <div className='flex items-center justify-between'>
                                <h4 className='text-sm font-medium'>Question {index + 1}</h4>
                                <Button
                                    variant='ghost'
                                    size='icon'
                                    type='button'
                                    onClick={() => onRemoveArrayItem?.(field.name, index, field)}
                                    disabled={!!field.min && arr.length <= field.min}
                                    className='h-8 w-8'
                                >
                                    <Trash2 />
                                </Button>
                            </div>
                            <div className='space-y-2'>
                                <div className='flex justify-between items-center'>
                                    <Label>Question</Label>
                                    <span
                                        className={`text-xs ${!isQuestionValid ? 'text-destructive' : 'text-muted-foreground'}`}
                                    >
                                        {questionLength}/100
                                    </span>
                                </div>
                                <Input
                                    placeholder='Enter your question'
                                    value={it.question ?? ''}
                                    onChange={e => {
                                        const newValue = e.target.value.slice(0, 100)
                                        onArrayChange?.(field.name, index, field, {
                                            ...it,
                                            question: newValue
                                        })
                                    }}
                                    className={!isQuestionValid ? 'border-destructive' : ''}
                                />
                            </div>
                            <div className='space-y-2'>
                                <div className='flex justify-between items-center'>
                                    <Label>Answer</Label>
                                    <span
                                        className={`text-xs ${!isAnswerValid ? 'text-destructive' : 'text-muted-foreground'}`}
                                    >
                                        {answerLength}/500
                                    </span>
                                </div>
                                <Textarea
                                    placeholder='Enter your answer'
                                    value={it.answer ?? ''}
                                    onChange={e => {
                                        const newValue = e.target.value.slice(0, 500)
                                        onArrayChange?.(field.name, index, field, {
                                            ...it,
                                            answer: newValue
                                        })
                                    }}
                                    rows={3}
                                    className={!isAnswerValid ? 'border-destructive' : ''}
                                />
                            </div>
                        </div>
                    )
                })}
                <div className='flex items-items mt-4 gap-2 flex-col'>
                    <Button
                        type='button'
                        onClick={() => onAddArrayItem?.(field.name, field)}
                        disabled={!!field.max && arr.length >= field.max}
                    >
                        Add FAQ Item
                    </Button>
                    <span>
                        {!!field.min && arr.length < field.min && (
                            <p className='text-destructive text-sm'>
                                Minimum {field.min} FAQ items required
                            </p>
                        )}
                        {field.max && arr.length >= field.max && (
                            <p className='text-warning text-sm'>
                                Maximum allowed FAQ items ({field.max}) reached
                            </p>
                        )}
                    </span>
                </div>
            </div>
        </FieldWrapper>
    )
}

export default FaqArrayField
